"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AlertCircle,
  Ban,
  Building,
  CheckCheck,
  ChevronUp,
  CreditCard,
  FileText,
  Headset,
  LayoutDashboard,
  LifeBuoy,
  LineChart,
  LogOut,
  Map,
  MapPin,
  Package,
  PackageX,
  PhoneOff,
  Pill,
  RotateCcw,
  Settings,
  Shield,
  Star,
  Stethoscope,
  Ticket,
  ToggleRight,
  Truck,
  User,
  Users,
  Wrench,
} from "lucide-react";

import type { NavSection } from "@/lib/types";

const iconMap: Record<string, LucideIcon> = {
  "layout-dashboard": LayoutDashboard,
  activity: Activity,
  ban: Ban,
  building: Building,
  check: CheckCheck,
  "credit-card": CreditCard,
  "file-text": FileText,
  headset: Headset,
  "life-buoy": LifeBuoy,
  "line-chart": LineChart,
  map: Map,
  package: Package,
  "phone-off": PhoneOff,
  pill: Pill,
  "rotate-ccw": RotateCcw,
  shield: Shield,
  star: Star,
  stethoscope: Stethoscope,
  truck: Truck,
  users: Users,
  "alert-circle": AlertCircle,
  "map-pin": MapPin,
  "package-x": PackageX,
  ticket: Ticket,
  "toggle-right": ToggleRight,
  wrench: Wrench,
};

const getIcon = (iconKey?: string) => iconMap[iconKey ?? ""] ?? LayoutDashboard;

function getInitials(name?: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

type SidebarProps = {
  navSections: NavSection[];
  userName?: string | null;
  onNavigate?: () => void;
};

export default function DashboardSidebar({
  navSections = [],
  userName,
  onNavigate,
}: SidebarProps) {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);

  const allHrefs = navSections.flatMap((s) => s.links.map((l) => l.href));
  const isActive = (href: string) =>
    pathname === href ||
    (href !== "/" &&
      pathname.startsWith(href) &&
      !allHrefs.some(
        (h) => h !== href && h.startsWith(href) && pathname.startsWith(h)
      ));

  return (
    <div
      className="sticky top-0 flex h-screen w-[236px] flex-shrink-0 flex-col overflow-hidden border-r border-sidebar-border bg-sidebar"
      style={{ paddingTop: 18 }}
    >
      {/* Brand */}
      <div className="border-b border-sidebar-border" style={{ margin: "0 12px", padding: "10px 14px 18px" }}>
        <Link href="/" onClick={onNavigate} className="block">
          <Image
            src="/sknv-logo.png"
            alt="SKNV — A Sincerus Pharmaceuticals Company"
            width={130}
            height={40}
            className="dark:brightness-0 dark:invert"
            style={{ display: "block", width: "100%", height: "auto", maxWidth: 130, opacity: 0.92 }}
            priority
          />
        </Link>
      </div>

      {/* Nav sections */}
      <div className="flex-1 overflow-y-auto overscroll-y-contain">
        <div className="px-[12px]">
        {navSections.map((section) => (
          <div key={section.section} className="mb-1">
            <p
              className="px-3 pb-1.5 pt-4 text-[10px] font-semibold uppercase text-muted-foreground"
              style={{ letterSpacing: "0.1em" }}
            >
              {section.section}
            </p>
            <nav className="space-y-px">
              {section.links.map((link) => {
                const Icon = getIcon(link.icon);
                const active = isActive(link.href);
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={onNavigate}
                    className={`flex items-center gap-[10px] rounded-[9px] px-[11px] py-2 text-[13px] font-medium transition-colors ${
                      active
                        ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-[0_1px_2px_rgba(46,30,22,0.04),0_2px_6px_rgba(46,30,22,0.05)]"
                        : "text-sidebar-foreground hover:bg-sidebar-accent"
                    }`}
                    data-active={active}
                  >
                    <Icon
                      className={`h-4 w-4 flex-shrink-0 ${
                        active ? "text-sidebar-primary-foreground" : "text-sidebar-foreground/70"
                      }`}
                    />
                    <span>{link.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        ))}
        </div>
      </div>

      {/* Account footer */}
      <div style={{ marginTop: "auto", position: "relative", padding: "0 12px" }}>
        {menuOpen && (
          <>
            <div
              onClick={() => setMenuOpen(false)}
              style={{ position: "fixed", inset: 0, zIndex: 10 }}
            />
            <div
              className="border border-border bg-popover"
              style={{
                position: "absolute",
                bottom: "calc(100% + 6px)",
                left: 0,
                right: 0,
                borderRadius: 9,
                boxShadow: "0 4px 10px rgba(46,30,22,0.06), 0 18px 44px rgba(46,30,22,0.12)",
                padding: 6,
                zIndex: 20,
              }}
            >
              <div className="border-b border-border" style={{ padding: "10px 10px 8px", marginBottom: 4 }}>
                <div className="text-popover-foreground" style={{ fontSize: 12.5, fontWeight: 600 }}>{userName ?? "Guest"}</div>
                <div className="text-muted-foreground" style={{ fontSize: 11, marginTop: 1 }}>Signed in</div>
              </div>
              {[
                { label: "Profile & preferences", Icon: User, href: "/settings" },
                { label: "Settings", Icon: Settings, href: "/settings" },
              ].map((item) => (
                <Link
                  key={item.label}
                  href={item.href}
                  onClick={() => {
                    setMenuOpen(false);
                    onNavigate?.();
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    padding: "7px 10px",
                    borderRadius: 6,
                    cursor: "pointer",
                    textDecoration: "none",
                    fontSize: 12.5,
                  }}
                  className="text-popover-foreground transition-colors hover:bg-sidebar-accent"
                >
                  <item.Icon size={14} className="text-muted-foreground" />
                  <span>{item.label}</span>
                </Link>
              ))}
              <div className="bg-border" style={{ height: 1, margin: "4px 0" }} />
              <Link
                href="/logout"
                onClick={() => {
                  setMenuOpen(false);
                  onNavigate?.();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: "7px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                  textDecoration: "none",
                  fontSize: 12.5,
                }}
                className="text-popover-foreground transition-colors hover:bg-sidebar-accent"
              >
                <LogOut size={14} className="text-muted-foreground" />
                <span>Sign out</span>
              </Link>
            </div>
          </>
        )}

        <button
          onClick={() => setMenuOpen((o) => !o)}
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            gap: 9,
            padding: "16px 12px",
            border: "none",
            cursor: "pointer",
            fontFamily: "inherit",
            textAlign: "left",
            borderRadius: 0,
            transition: "background 0.13s",
          }}
          className={`border-t border-sidebar-border hover:bg-sidebar-accent ${
            menuOpen ? "bg-sidebar-accent" : "bg-transparent"
          }`}
        >
          <div
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
            style={{ background: "linear-gradient(135deg, #673D36, #BC6B59)" }}
          >
            {getInitials(userName)}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="text-sidebar-foreground" style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {userName ?? "Guest"}
            </div>
          </div>
          <ChevronUp
            size={14}
            className="text-muted-foreground"
            style={{
              transform: menuOpen ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.15s",
            }}
          />
        </button>
      </div>
    </div>
  );
}
